/**
 * Language name normalization for ISO 639-1 codes
 *
 * Maps 2-letter language codes (e.g., "en") to full language names (e.g., "English")
 */

// Map ISO 639-1 language codes to full names
const LANGUAGE_NAME_MAP: Record<string, string> = {
  // Major languages (by number of speakers and content production)
  en: "English",
  es: "Spanish",
  fr: "French",
  de: "German",
  it: "Italian",
  pt: "Portuguese",
  ru: "Russian",
  ja: "Japanese",
  ko: "Korean",
  zh: "Chinese",
  ar: "Arabic",
  hi: "Hindi",

  // European languages
  nl: "Dutch",
  sv: "Swedish",
  no: "Norwegian",
  da: "Danish",
  fi: "Finnish",
  pl: "Polish",
  tr: "Turkish",
  he: "Hebrew",
  cs: "Czech",
  hu: "Hungarian",
  ro: "Romanian",
  el: "Greek",
  uk: "Ukrainian",
  bg: "Bulgarian",
  sr: "Serbian",
  hr: "Croatian",
  sk: "Slovak",
  sl: "Slovenian",
  lt: "Lithuanian",
  lv: "Latvian",
  et: "Estonian",
  is: "Icelandic",
  ga: "Irish",
  cy: "Welsh",
  eu: "Basque",
  ca: "Catalan",
  gl: "Galician",

  // Asian languages
  th: "Thai",
  vi: "Vietnamese",
  id: "Indonesian",
  ms: "Malay",
  fa: "Persian",
  bn: "Bengali",
  ur: "Urdu",
  ta: "Tamil",
  te: "Telugu",
  mr: "Marathi",
  kn: "Kannada",
  ml: "Malayalam",
  pa: "Punjabi",
  gu: "Gujarati",
  ne: "Nepali",
  si: "Sinhala",
  my: "Burmese",
  km: "Khmer",
  lo: "Lao",

  // Other major languages
  sw: "Swahili",
  am: "Amharic",
  ha: "Hausa",
  yo: "Yoruba",
  ig: "Igbo",
  zu: "Zulu",
  af: "Afrikaans",
  sq: "Albanian",
  hy: "Armenian",
  az: "Azerbaijani",
  ka: "Georgian",
  kk: "Kazakh",
  uz: "Uzbek",
  mn: "Mongolian",
};

/**
 * Convert a string to proper Title Case
 * @param str - String to convert
 * @returns Title cased string
 */
function toTitleCase(str: string): string {
  return str
    .toLowerCase()
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

/**
 * Check if a string is likely already a full language name
 * @param str - String to check
 * @returns True if it appears to be a full language name
 */
function isFullLanguageName(str: string): boolean {
  // If it's longer than 3 characters and contains letters, likely a full name
  return str.length > 3 && /^[a-zA-Z\s]+$/.test(str);
}

/**
 * Normalize a language code to its full name
 * @param code - ISO 639-1 language code (e.g., "en", "es", "fr") or full name
 * @returns Full language name in proper case (e.g., "English", "Spanish", "French")
 */
export function normalizeLanguageName(code: string): string {
  const trimmedCode = code.trim();

  // Check if we have a mapping for the code (case-insensitive)
  const lowerCode = trimmedCode.toLowerCase();
  if (LANGUAGE_NAME_MAP[lowerCode]) {
    return LANGUAGE_NAME_MAP[lowerCode];
  }

  // If it's already a full language name, return it in proper title case
  if (isFullLanguageName(trimmedCode)) {
    return toTitleCase(trimmedCode);
  }

  // Return the code in title case if no mapping found
  return toTitleCase(trimmedCode);
}

/**
 * Normalize multiple language codes to their full names, removing duplicates
 * @param codes - Array of ISO 639-1 language codes or full names
 * @returns Array of unique full language names in proper case
 */
export function normalizeLanguageNames(codes: string[]): string[] {
  // Normalize each code and collect unique results
  const normalizedSet = new Set<string>();

  for (const code of codes) {
    if (code?.trim()) {
      const normalized = normalizeLanguageName(code);
      normalizedSet.add(normalized);
    }
  }

  // Convert back to array and sort for consistent display
  return Array.from(normalizedSet).sort((a, b) => a.localeCompare(b));
}

/**
 * Language options for use in select/combobox components
 * Exported as array of {value: ISO code, label: full name} sorted by label
 */
export const LANGUAGE_OPTIONS = Object.entries(LANGUAGE_NAME_MAP)
  .map(([value, label]) => ({ value, label }))
  .sort((a, b) => a.label.localeCompare(b.label));
